import { useState, useRef, useEffect } from "react";
import { Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { useNavigate } from "react-router-dom";

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  inquiry_id: string | null;
  is_read: boolean;
  created_at: string;
}

const SUPPLIER_INQUIRY_NOTIFICATION_TYPES = new Set(["approval", "inquiry"]);
const CUSTOMER_INQUIRY_NOTIFICATION_TYPES = new Set([
  "approval_confirmed",
  "inquiry_submitted",
  "new_quote",
  "quote",
]);

export function NotificationBell({ dropUp = false }: { dropUp?: boolean }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);
      if (data) setNotifications(data as Notification[]);
    };
    load();

    const channel = supabase
      .channel("notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => {
          setNotifications((prev) => [payload.new as Notification, ...prev]);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAsRead = async (id: string) => {
    await supabase.from("notifications").update({ is_read: true }).eq("id", id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
  };

  const handleClick = async (n: Notification) => {
    markAsRead(n.id);
    if (n.type === "customer_message") {
      navigate("/supplier/dashboard?messages=1");
    } else if (n.inquiry_id) {
      if (SUPPLIER_INQUIRY_NOTIFICATION_TYPES.has(n.type)) {
        navigate(`/supplier/inquiry/${n.inquiry_id}`);
      } else if (CUSTOMER_INQUIRY_NOTIFICATION_TYPES.has(n.type)) {
        navigate(`/dashboard/${n.inquiry_id}`);
      }
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-accent transition-colors text-muted-foreground"
        title={t("notifications.title")}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute ${dropUp ? "bottom-full mb-1.5" : "top-full mt-1.5"} right-0 bg-popover border border-border rounded-xl shadow-lg py-1 min-w-[280px] max-w-[340px] max-h-[400px] overflow-y-auto z-50`}>
          <p className="px-4 py-2 text-sm font-medium text-foreground border-b border-border">
            {t("notifications.title")}
          </p>
          {!user ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-muted-foreground mb-3">
                {t("notifications.loginPrompt")}
              </p>
              <button
                onClick={() => { setOpen(false); navigate("/login"); }}
                className="px-4 py-1.5 text-sm font-medium border border-border text-foreground rounded-full hover:bg-accent transition-colors"
              >
                {t("topbar.login")}
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground text-center">
              {t("notifications.empty")}
            </p>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-4 py-3 hover:bg-accent transition-colors border-b border-border last:border-b-0 ${
                  !n.is_read ? "bg-accent/30" : ""
                }`}
              >
                <p className={`text-sm ${!n.is_read ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                  {n.title}
                </p>
                {n.body && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                )}
                <p className="text-[10px] text-muted-foreground mt-1">
                  {new Date(n.created_at).toLocaleDateString()}
                </p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
